/**
 * Iter-215 · Constitution-aware Financial Statements component that lives
 * inside the Reports page. Replaces the previous split of P&L / BS /
 * P&L(S3) / BS(S3) / (separate) Financial Statements page. Every row is
 * clickable to drill down to the underlying accounts + vouchers.
 *
 * Data flow:
 *   1. GET /financial-statements/family       → { entity_type, family, ... }
 *   2. GET /financial-statements/balance-sheet → constitution-aware BS with
 *                                                 `line_details` for drill
 *   3. GET /financial-statements/pnl           → constitution-aware P&L
 *
 * Shape rendered per family:
 *   • company     → Schedule III (Companies Act 2013)
 *   • llp         → LLP Rule 24 (ICAI Guidance Note format)
 *   • partnership → ICAI GN Non-Corporate Entities
 *   • proprietor  → Simplified (proprietor capital)
 *   • ngo         → Schedule VIII
 */
import React, { useContext, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Download, Settings2 } from "lucide-react";
import { fmtINR } from "@/lib/format";
import { downloadBlob } from "@/lib/download";
import { DrillCtx, EntryChip } from "@/components/reports/DrillDownDrawer";
import ScheduleIIINotes from "@/pages/ScheduleIIINotes";
import StatementFormatEditor from "@/pages/reports/StatementFormatEditor";
import ESignButton from "@/components/esign/ESignButton";

/** Human-friendly caption for a Sch III / LLP bucket key. */
const CAP = {
  // BS
  equity_share_capital: "Share Capital / Partners' Contribution",
  reserves_surplus: "Reserves & Surplus",
  long_term_borrowings: "Long-term Borrowings",
  deferred_tax_liabilities: "Deferred Tax Liabilities (Net)",
  long_term_provisions: "Long-term Provisions",
  short_term_borrowings: "Short-term Borrowings",
  trade_payables: "Trade Payables",
  other_current_liabilities: "Other Current Liabilities",
  short_term_provisions: "Short-term Provisions",
  fixed_assets_tangible: "Tangible Fixed Assets",
  fixed_assets_intangible: "Intangible Assets",
  capital_wip: "Capital Work-in-Progress",
  noncurrent_investments: "Non-current Investments",
  long_term_loans_advances: "Long-term Loans & Advances",
  current_investments: "Current Investments",
  inventories: "Inventories",
  trade_receivables: "Trade Receivables",
  cash_bank: "Cash & Cash Equivalents",
  short_term_loans_advances: "Short-term Loans & Advances",
  other_current_assets: "Other Current Assets",
  // P&L
  revenue_from_operations: "Revenue from Operations",
  other_income: "Other Income",
  cost_of_materials: "Cost of Materials consumed",
  changes_in_inventories: "Changes in Inventories",
  employee_benefit_expense: "Employee Benefit Expense",
  finance_costs: "Finance Costs",
  depreciation_amortisation: "Depreciation & Amortisation",
  other_expenses: "Other Expenses",
};

/** BS section layout per constitution — each element is
 *  {title, keys[]} where keys refer to Sch III bucket names in `line_details`. */
const _BS_SECTIONS = {
  company: {
    left:  { title: "Equity & Liabilities", groups: [
      { label: "Shareholders' Funds", keys: ["equity_share_capital", "reserves_surplus"] },
      { label: "Non-Current Liabilities", keys: ["long_term_borrowings", "deferred_tax_liabilities", "long_term_provisions"] },
      { label: "Current Liabilities", keys: ["short_term_borrowings", "trade_payables", "other_current_liabilities", "short_term_provisions"] },
    ]},
    right: { title: "Assets", groups: [
      { label: "Non-Current Assets", keys: ["fixed_assets_tangible", "fixed_assets_intangible", "capital_wip", "noncurrent_investments", "long_term_loans_advances"] },
      { label: "Current Assets", keys: ["current_investments", "inventories", "trade_receivables", "cash_bank", "short_term_loans_advances", "other_current_assets"] },
    ]},
  },
  llp: {
    left:  { title: "I. Contribution & Liabilities", groups: [
      { label: "Partners' Funds", keys: ["equity_share_capital", "reserves_surplus"] },
      { label: "Loans (incl. Partner Loans bifurcated)", keys: ["long_term_borrowings", "short_term_borrowings"] },
      { label: "Current Liabilities & Provisions", keys: ["trade_payables", "other_current_liabilities", "long_term_provisions", "short_term_provisions", "deferred_tax_liabilities"] },
    ]},
    right: { title: "II. Assets", groups: [
      { label: "Fixed Assets", keys: ["fixed_assets_tangible", "fixed_assets_intangible", "capital_wip"] },
      { label: "Investments", keys: ["noncurrent_investments", "current_investments"] },
      { label: "Loans & Advances (incl. Loans to Partners)", keys: ["long_term_loans_advances", "short_term_loans_advances"] },
      { label: "Current Assets", keys: ["inventories", "trade_receivables", "cash_bank", "other_current_assets"] },
    ]},
  },
  // Partnership + proprietor + ngo share the base Sch III layout with a
  // few tweaks; keep them same as `company` for now (backend already
  // reshapes labels in `render_balance_sheet`).
  partnership: {
    left:  { title: "Capital & Liabilities", groups: [
      { label: "Partners' Capital", keys: ["equity_share_capital", "reserves_surplus"] },
      { label: "Loans", keys: ["long_term_borrowings", "short_term_borrowings"] },
      { label: "Current Liabilities", keys: ["trade_payables", "other_current_liabilities", "long_term_provisions", "short_term_provisions"] },
    ]},
    right: { title: "Assets", groups: [
      { label: "Fixed Assets", keys: ["fixed_assets_tangible", "fixed_assets_intangible", "capital_wip"] },
      { label: "Investments & Loans", keys: ["noncurrent_investments", "long_term_loans_advances"] },
      { label: "Current Assets", keys: ["current_investments", "inventories", "trade_receivables", "cash_bank", "short_term_loans_advances", "other_current_assets"] },
    ]},
  },
  proprietor: null,   // handled via `company` fallback
  ngo: null,
};

function SectionBlock({ side, ld, drill, asOf, familyLabel }) {
  const total = (side.groups || []).reduce((sum, g) => {
    return sum + (g.keys || []).reduce((s2, k) => {
      const rows = ld[k] || [];
      return s2 + rows.reduce((s3, r) => s3 + (Number(r.amount) || 0), 0);
    }, 0);
  }, 0);
  return (
    <div className="card-sharp p-5" data-testid={`fs-side-${side.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 24)}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">{side.title}</div>
      <table className="w-full dense">
        <tbody>
          {(side.groups || []).map((g) => {
            const groupRows = (g.keys || []).filter((k) => (ld[k] || []).length);
            if (!groupRows.length) return null;
            const groupTotal = groupRows.reduce((s, k) => (ld[k] || []).reduce((s2, r) => s2 + (Number(r.amount) || 0), s), 0);
            return (
              <React.Fragment key={g.label}>
                <tr>
                  <td colSpan="2" className="pt-3 pb-1 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">{g.label}</td>
                </tr>
                {groupRows.map((k) => {
                  const rows = ld[k] || [];
                  const bucketAmt = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
                  const entryCount = rows.reduce((s, r) => s + (r.entry_count || 0), 0);
                  return (
                    <tr
                      key={k}
                      className="cursor-pointer group hover:bg-muted/40"
                      onClick={() => drill.openSubcategory(`${CAP[k] || k} · ${fmtINR(bucketAmt)}`, rows)}
                      data-testid={`fs-bs-row-${k}`}
                    >
                      <td className="py-1.5 pl-4 text-sm">
                        <span className="group-hover:text-accent group-hover:underline decoration-dotted underline-offset-2">{CAP[k] || k}</span>
                        {entryCount > 0 && <EntryChip count={entryCount} testid={`fs-bs-chip-${k}`} />}
                      </td>
                      <td className="py-1.5 text-right tabular-nums font-mono">{fmtINR(bucketAmt)}</td>
                    </tr>
                  );
                })}
                <tr className="bg-muted/30 border-t border-border/60 font-medium">
                  <td className="py-1 pl-4 text-xs">Sub-total · {g.label}</td>
                  <td className="py-1 text-right tabular-nums font-mono">{fmtINR(groupTotal)}</td>
                </tr>
              </React.Fragment>
            );
          })}
          <tr className="border-t-2 border-primary">
            <td className="py-2 font-bold">TOTAL</td>
            <td className="py-2 text-right tabular-nums font-mono font-bold">{fmtINR(total)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function BalanceSheetPanel({ family, familyLabel, asOf }) {
  const drill = useContext(DrillCtx);
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!asOf) return;
    api.get("/financial-statements/balance-sheet", { params: { as_of: asOf } }).then((r) => setD(r.data));
  }, [asOf]);
  const sections = _BS_SECTIONS[family] || _BS_SECTIONS.company;
  if (!d) return <div className="p-6 text-muted-foreground">Loading Balance Sheet…</div>;
  const ld = d.line_details || {};
  const balanced = d.balanced;
  const eqTotal = d.equity_and_liabilities?.grand_total || 0;
  const asTotal = d.assets?.grand_total || 0;
  return (
    <div className="space-y-4" data-testid="fs-bs-panel">
      <div className="card-sharp p-3 border-l-4 border-l-accent text-xs bg-accent/5">
        <b>{familyLabel}</b> · Balance Sheet as of <b>{d.as_of}</b> · Click any bucket to drill into contributing ledgers → vouchers.
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <SectionBlock side={sections.left} ld={ld} drill={drill} asOf={d.as_of} familyLabel={familyLabel} />
        <SectionBlock side={sections.right} ld={ld} drill={drill} asOf={d.as_of} familyLabel={familyLabel} />
      </div>
      <div className="text-xs text-muted-foreground flex items-center gap-2" data-testid="fs-bs-balanced">
        {balanced
          ? <span className="text-emerald-600">✓ Balance Sheet is balanced</span>
          : <span className="text-red-600">⚠ Not balanced — difference = {fmtINR(eqTotal - asTotal)}</span>}
      </div>
      {d.disclosure_note && (
        <div className="card-sharp p-3 border-l-4 border-l-accent bg-muted/30 text-xs italic text-muted-foreground">
          <b>Disclosure:</b> {d.disclosure_note}
        </div>
      )}
    </div>
  );
}

function ProfitLossPanel({ family, familyLabel, fromDate, toDate }) {
  const drill = useContext(DrillCtx);
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!fromDate || !toDate) return;
    api.get("/financial-statements/pnl", { params: { from_date: fromDate, to_date: toDate } }).then((r) => setD(r.data));
  }, [fromDate, toDate]);
  if (!d) return <div className="p-6 text-muted-foreground">Loading P&amp;L…</div>;
  const ld = d.line_details || {};
  const revenueKeys = ["revenue_from_operations", "other_income"].filter((k) => (ld[k] || []).length);
  const expenseKeys = ["cost_of_materials", "changes_in_inventories", "employee_benefit_expense", "finance_costs", "depreciation_amortisation", "other_expenses"].filter((k) => (ld[k] || []).length);
  const bucketTotal = (k) => (ld[k] || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const openBucket = (k) => {
    const rows = ld[k] || [];
    const t = rows.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    drill.openSubcategory(`${CAP[k] || k} · ${fmtINR(t)}`, rows);
  };
  const totalRev = revenueKeys.reduce((s, k) => s + bucketTotal(k), 0);
  const totalExp = expenseKeys.reduce((s, k) => s + bucketTotal(k), 0);
  const pbt = totalRev - totalExp;
  const isPartnerLike = family === "llp" || family === "partnership";
  const pd = d.partner_disclosures || {};
  const ap = d.appropriations || {};
  return (
    <div className="space-y-4" data-testid="fs-pl-panel">
      <div className="card-sharp p-3 border-l-4 border-l-accent text-xs bg-accent/5">
        <b>{familyLabel}</b> · Profit &amp; Loss for <b>{fromDate}</b> → <b>{toDate}</b> · Click any bucket to drill into contributing ledgers.
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-sharp p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-600 mb-3">Revenue</div>
          <table className="w-full dense">
            <tbody>
              {revenueKeys.length === 0 && <tr><td colSpan="2" className="text-muted-foreground text-center py-3">No revenue recorded in this period.</td></tr>}
              {revenueKeys.map((k) => {
                const rows = ld[k] || [];
                const t = bucketTotal(k);
                const ec = rows.reduce((s, r) => s + (r.entry_count || 0), 0);
                return (
                  <tr key={k} className="cursor-pointer group hover:bg-muted/40" onClick={() => openBucket(k)} data-testid={`fs-pl-rev-${k}`}>
                    <td className="py-1.5 pl-4"><span className="group-hover:text-accent group-hover:underline decoration-dotted underline-offset-2">{CAP[k] || k}</span>{ec > 0 && <EntryChip count={ec} />}</td>
                    <td className="py-1.5 text-right tabular-nums font-mono">{fmtINR(t)}</td>
                  </tr>
                );
              })}
              <tr className="border-t-2"><td className="py-2 font-semibold">Total Revenue</td><td className="py-2 text-right tabular-nums font-mono font-semibold">{fmtINR(totalRev)}</td></tr>
            </tbody>
          </table>
        </div>
        <div className="card-sharp p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Expenses</div>
          <table className="w-full dense">
            <tbody>
              {expenseKeys.length === 0 && <tr><td colSpan="2" className="text-muted-foreground text-center py-3">No expenses recorded in this period.</td></tr>}
              {expenseKeys.map((k) => {
                const rows = ld[k] || [];
                const t = bucketTotal(k);
                const ec = rows.reduce((s, r) => s + (r.entry_count || 0), 0);
                return (
                  <tr key={k} className="cursor-pointer group hover:bg-muted/40" onClick={() => openBucket(k)} data-testid={`fs-pl-exp-${k}`}>
                    <td className="py-1.5 pl-4"><span className="group-hover:text-accent group-hover:underline decoration-dotted underline-offset-2">{CAP[k] || k}</span>{ec > 0 && <EntryChip count={ec} />}</td>
                    <td className="py-1.5 text-right tabular-nums font-mono">{fmtINR(t)}</td>
                  </tr>
                );
              })}
              {isPartnerLike && (
                <>
                  <tr><td colSpan="2" className="pt-2 text-[10px] font-mono uppercase text-purple-700">Partner-related (per ICAI GN)</td></tr>
                  <tr><td className="py-1.5 pl-4 text-sm">Remuneration to Partners</td><td className="py-1.5 text-right tabular-nums font-mono">{fmtINR(pd.remuneration_to_partners || 0)}</td></tr>
                  <tr><td className="py-1.5 pl-4 text-sm">Interest on Partners' Capital</td><td className="py-1.5 text-right tabular-nums font-mono">{fmtINR(pd.interest_on_partners_capital || 0)}</td></tr>
                </>
              )}
              <tr className="border-t-2"><td className="py-2 font-semibold">Total Expenses</td><td className="py-2 text-right tabular-nums font-mono font-semibold">{fmtINR(totalExp)}</td></tr>
            </tbody>
          </table>
        </div>
      </div>
      <div className="card-sharp p-5 bg-primary text-primary-foreground flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">{family === "company" ? "Profit / (Loss) After Tax" : "Profit / (Loss) for the year"}</div>
          <div className="text-xs opacity-60 mt-1">Revenue − Expenses</div>
        </div>
        <div className={`metric-value text-4xl ${pbt >= 0 ? "" : "text-red-400"}`} data-testid="fs-pl-net-profit">{fmtINR(pbt)}</div>
      </div>
      {isPartnerLike && (ap.share_of_profit_to_partners || []).length > 0 && (
        <div className="card-sharp p-5 border-l-4 border-l-purple-500 bg-purple-50/30" data-testid="fs-pl-appro">
          <div className="text-[10px] font-mono uppercase tracking-widest text-purple-700 mb-2">Appropriations · Share of Profit distributed to Partners</div>
          <table className="w-full dense">
            <thead><tr><th className="text-left text-[10px] font-mono uppercase text-muted-foreground py-1">Partner</th><th className="text-right text-[10px] font-mono uppercase text-muted-foreground py-1">Share %</th><th className="text-right text-[10px] font-mono uppercase text-muted-foreground py-1">Amount</th></tr></thead>
            <tbody>
              {(ap.share_of_profit_to_partners || []).map((r) => (
                <tr key={r.partner} className="border-t border-border/40">
                  <td className="py-1">{r.partner}</td>
                  <td className="py-1 text-right tabular-nums font-mono">{r.share_pct}%</td>
                  <td className="py-1 text-right tabular-nums font-mono">{fmtINR(r.amount)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-purple-500 bg-purple-100/40 font-semibold"><td className="py-1.5">Total Appropriated</td><td></td><td className="py-1.5 text-right tabular-nums font-mono">{fmtINR(ap.total_appropriated)}</td></tr>
            </tbody>
          </table>
          {ap.note && <div className="text-xs italic text-muted-foreground mt-2">{ap.note}</div>}
        </div>
      )}
    </div>
  );
}

/** Constitution-aware Notes tab — Sch III Notes for Companies, LLP / Partnership
 * ICAI GN disclosures for LLP + Partnership. */
export function NotesConstitutionAware({ family, familyLabel }) {
  // Company uses the rich Sch III Notes page (ratios, ageing, related-party
  // etc.). LLP + Partnership + Proprietor + NGO — use a lighter view driven
  // by the same balance-sheet disclosure_note + partner disclosures.
  if (family === "company") return <ScheduleIIINotes />;
  return <NotesLLP family={family} familyLabel={familyLabel} />;
}

// Local import (kept at file bottom so the export above works without a
// forward reference issue).
// (moved to top-of-file — kept comment for git-blame trace)

function NotesLLP({ family, familyLabel }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    api.get("/financial-statements/pnl").then((r) => setD(r.data));
  }, []);
  if (!d) return <div className="p-6 text-muted-foreground">Loading disclosures…</div>;
  const pd = d.partner_disclosures || {};
  const ap = d.appropriations || {};
  return (
    <div className="space-y-4" data-testid="fs-notes-llp">
      <div className="card-sharp p-3 border-l-4 border-l-accent text-xs bg-accent/5">
        <b>{familyLabel}</b> · Constitution-specific disclosures rendered per ICAI Guidance Note.
      </div>
      <div className="card-sharp p-5">
        <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Partner-related P&amp;L Disclosures</div>
        <table className="w-full dense">
          <tbody>
            <tr className="border-b"><td className="py-2">Remuneration to Partners</td><td className="text-right font-mono">{fmtINR(pd.remuneration_to_partners || 0)}</td></tr>
            <tr className="border-b"><td className="py-2">Interest on Partners' Capital</td><td className="text-right font-mono">{fmtINR(pd.interest_on_partners_capital || 0)}</td></tr>
          </tbody>
        </table>
      </div>
      {(ap.share_of_profit_to_partners || []).length > 0 && (
        <div className="card-sharp p-5">
          <div className="text-[10px] font-mono uppercase tracking-widest text-accent mb-3">Appropriations · Share of Profit</div>
          <table className="w-full dense">
            <thead><tr><th className="text-left">Partner</th><th className="text-right">Share %</th><th className="text-right">Amount</th></tr></thead>
            <tbody>
              {(ap.share_of_profit_to_partners || []).map((r) => (
                <tr key={r.partner} className="border-b"><td className="py-1">{r.partner}</td><td className="py-1 text-right font-mono">{r.share_pct}%</td><td className="py-1 text-right font-mono">{fmtINR(r.amount)}</td></tr>
              ))}
              <tr className="font-semibold border-t-2"><td className="py-1">Total Appropriated</td><td></td><td className="py-1 text-right font-mono">{fmtINR(ap.total_appropriated || 0)}</td></tr>
            </tbody>
          </table>
          {ap.note && <div className="text-xs italic text-muted-foreground mt-2">{ap.note}</div>}
        </div>
      )}
      <div className="card-sharp p-3 border-l-4 border-l-accent bg-muted/30 text-xs italic text-muted-foreground">
        <b>ICAI GN reference:</b> Non-Corporate Entities Guidance Note prescribes disclosures for Partners' Contribution movement, Interest on Capital, Remuneration, Drawings and Share-of-Profit allocation. Contingent Liabilities and Related-Party disclosures apply under AS-29 / AS-18 respectively.
      </div>
    </div>
  );
}

export default function ConstitutionAwareFS({ asOf, fromDate, toDate }) {
  const [fam, setFam] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    api.get("/financial-statements/family").then((r) => setFam(r.data)).catch(() => setFam({ family: "company", family_label: "Schedule III · Companies Act 2013" }));
  }, []);
  const family = fam?.family || "company";
  const familyLabel = fam?.family_label || "";
  const downloadPack = () => {
    downloadBlob(
      `/financial-statements/pack.xlsx?as_of=${asOf}&from_date=${fromDate}&to_date=${toDate}`,
      `Financial_Statements_Pack_${fromDate}_${toDate}.xlsx`,
    );
  };
  const downloadPdf = () => {
    downloadBlob(
      `/financial-statements/pack.pdf?as_of=${asOf}&from_date=${fromDate}&to_date=${toDate}`,
      `Financial_Statements_Pack_${fromDate}_${toDate}.pdf`,
    );
  };
  return (
    <div className="space-y-4" data-testid="constitution-aware-fs">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-muted-foreground">
          Entity: <b>{fam?.org_name}</b> · Constitution: <b>{fam?.entity_type}</b> · Format: <b>{familyLabel}</b>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setEditorOpen(true)} className="rounded-sm gap-1.5" data-testid="fs-format-editor-btn">
            <Settings2 className="w-3.5 h-3.5" /> Format Editor
          </Button>
          <Button size="sm" variant="outline" onClick={downloadPack} className="rounded-sm gap-1.5" data-testid="fs-download-xlsx">
            <Download className="w-3.5 h-3.5" /> Traceable Excel Pack
          </Button>
          <Button size="sm" variant="outline" onClick={downloadPdf} className="rounded-sm gap-1.5" data-testid="fs-download-pdf">
            <Download className="w-3.5 h-3.5" /> PDF Pack (with ToC)
          </Button>
          <ESignButton
            pdfPath={`/financial-statements/pack.pdf?as_of=${asOf}&from_date=${fromDate}&to_date=${toDate}`}
            fileName={`Financial_Statements_${fromDate}_${toDate}.pdf`}
            docType="financial_statements" docRef={`${fromDate}_${toDate}`}
            title={`Financial Statements ${fromDate} to ${toDate}`}
            testId="fs-esign-btn"
          />
        </div>
      </div>
      <Tabs defaultValue="bs">
        <TabsList className="rounded-sm">
          <TabsTrigger value="bs" className="rounded-sm" data-testid="fs-tab-bs">Balance Sheet</TabsTrigger>
          <TabsTrigger value="pl" className="rounded-sm" data-testid="fs-tab-pl">Profit &amp; Loss</TabsTrigger>
          <TabsTrigger value="notes" className="rounded-sm" data-testid="fs-tab-notes">Notes &amp; Disclosures</TabsTrigger>
        </TabsList>
        <TabsContent value="bs" className="mt-4"><BalanceSheetPanel key={`bs-${refreshKey}`} family={family} familyLabel={familyLabel} asOf={asOf} /></TabsContent>
        <TabsContent value="pl" className="mt-4"><ProfitLossPanel key={`pl-${refreshKey}`} family={family} familyLabel={familyLabel} fromDate={fromDate} toDate={toDate} /></TabsContent>
        <TabsContent value="notes" className="mt-4"><NotesConstitutionAware family={family} familyLabel={familyLabel} /></TabsContent>
      </Tabs>
      <StatementFormatEditor open={editorOpen} onOpenChange={setEditorOpen} onSaved={() => setRefreshKey((k) => k + 1)} />
    </div>
  );
}
